import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, Field, Sel, GREEN } from '../bfsi/bfsiShared';
import { qrPost } from './qrApi';

export function IssuePassportsForm({ customers, profiles, onIssued, tenantId: fixedTenantId }) {
  const [ownTenantId, setOwnTenantId] = useState('');
  const tenantId = fixedTenantId ?? ownTenantId;  // controlled by the QR labels screen's customer selector when given
  const setTenantId = setOwnTenantId;
  const [count, setCount] = useState('100');
  const [profileId, setProfileId] = useState('');
  const [cartons, setCartons] = useState(false);
  const [perParent, setPerParent] = useState('10');
  const [busy, setBusy] = useState(false);

  const n = parseInt(count, 10) || 0;
  const cpp = parseInt(perParent, 10) || 0;
  const invalid = !tenantId || n < 1 || n > 5000 || (cartons && (cpp < 1 || n % cpp !== 0));

  const submit = async () => {
    setBusy(true);
    try {
      const r = await qrPost('/passports/issue', {
        tenant_id: tenantId, count: n, profile_id: profileId || null,
        parent_structure: cartons ? { children_per_parent: cpp } : null,
      });
      toast.success(`Issued ${r.count} passports${r.parents ? ` in ${r.parents} cartons` : ''} (${r.elapsed_ms} ms)`);
      onIssued(r);
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  return (
    <Card data-testid="qr-issue-form">
      <h2 className="text-base md:text-lg font-semibold mb-4" style={{ color: '#1B1D1C' }}>Issue passports</h2>
      <div className={`grid ${fixedTenantId ? 'md:grid-cols-3' : 'md:grid-cols-4'} gap-4`}>
        {!fixedTenantId && <Sel label="Customer (custodian)" value={tenantId} onChange={setTenantId} testId="qr-issue-tenant"
          options={customers.map(c => [c.id, c.name])} />}
        <Field label="Count (1–5000)" value={count} onChange={setCount} testId="qr-issue-count" type="number" min={1} max={5000} />
        <Sel label="Temperature profile" value={profileId} onChange={setProfileId} testId="qr-issue-profile"
          options={profiles.map(p => [p.id, `${p.name} · ${p.max_storage_days} d`])} />
        <div>
          <label className="text-sm block mb-1" style={{ color: '#4b504a' }}>Carton structure</label>
          <div className="flex items-center gap-2 h-9">
            <input id="qr-cartons" data-testid="qr-issue-cartons" type="checkbox" checked={cartons} onChange={e => setCartons(e.target.checked)} />
            <label htmlFor="qr-cartons" className="text-sm">children per parent</label>
            <input data-testid="qr-issue-per-parent" type="number" min={1} disabled={!cartons} value={perParent}
              onChange={e => setPerParent(e.target.value)} className="w-16 h-8 rounded border px-2 text-sm" style={{ borderColor: '#D9D6CD' }} />
          </div>
        </div>
      </div>
      <div className="flex items-center gap-3 mt-4">
        <Button data-testid="qr-issue-submit" onClick={submit} disabled={busy || invalid} className="h-9 text-white" style={{ background: GREEN }}>
          {busy ? 'Issuing…' : 'Issue'}
        </Button>
        {cartons && n > 0 && cpp > 0 && (
          <span className="text-xs" style={{ color: n % cpp ? '#B42318' : '#6b7069' }} data-testid="qr-issue-hint">
            {n % cpp ? `count must be a multiple of ${cpp}` : `${n / cpp} parents + ${n} children`}
          </span>
        )}
      </div>
    </Card>
  );
}
